import classNames from "classnames";
import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import { escape as escapeHtml } from "lodash";

import { useFormatMessage } from "../Intl/Context";
import { useSessionState } from "../Utils/useSessionState";
import { hasHtmlContent } from "../Utils/htmlStringUtils";
import Icon from "../Utils/Icon";
import Button from "../Buttons/Button";
import FrontEditor from "../Wysiwyg/FrontEditor";
import SpeechToTextRecorder from "./SpeechToTextRecorder";

// Consent to the transcription notice is asked only once per browsing session.
// We persist the acceptance in sessionStorage (rather than in component state)
// so a user who already accepted on one field is not prompted again when they
// record on another field within the same session.
const CONSENT_STORAGE_KEY = "speechToText.transcriptionConsentAccepted";

const deserializeConsent = value => value === "true";

const appendTranscriptionToRichTextValue = (currentValue, transcription) => {
  const normalizedTranscription = (transcription || "").trim();
  if (!normalizedTranscription) return currentValue || "";

  const escapedTranscription = escapeHtml(normalizedTranscription).replace(/\n/g, "<br>");
  const transcriptionBlock = `<p>${escapedTranscription}</p>`;

  if (!hasHtmlContent(currentValue || "")) {
    return transcriptionBlock;
  }

  return `${currentValue}${transcriptionBlock}`;
};

const SpeechToTextRichTextInput = ({
  attachmentToken,
  labelNode,
  resourceId,
  value,
  onChange,
  onFocus
}) => {
  const t = useFormatMessage();
  const [isRecording, setIsRecording] = useState(false);
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(false);
  const [isTranscriptionNoticeVisible, setTranscriptionNoticeVisible] = useState(false);
  const [hasAcceptedConsent, setHasAcceptedConsent] = useSessionState(
    CONSENT_STORAGE_KEY,
    false,
    { deserialize: deserializeConsent }
  );

  const canRecord = !!resourceId;
  const isEditorVisible = !isRecording && !isOnboardingVisible;
  const laterLabel = t(intlMessages.later);
  const startFromOnboardingLabel = t(intlMessages.startFromOnboarding);
  const startRecordingLabel = t(intlMessages.startRecording);

  const dismissOnboarding = useCallback(() => {
    setIsOnboardingVisible(false);
  }, []);

  const handleOpenSpeechInput = useCallback(() => {
    if (!canRecord) return;

    // Reset the previous "transcription completed" notice: keeping it visible
    // while the user starts a new recording would falsely suggest a fresh
    // transcription just landed.
    setTranscriptionNoticeVisible(false);

    // Skip the consent onboarding when the user already accepted it earlier in
    // the session and go straight to recording.
    if (hasAcceptedConsent) {
      setIsRecording(true);
    } else {
      setIsOnboardingVisible(true);
    }
  }, [canRecord, hasAcceptedConsent]);

  const handleStartRecording = useCallback(() => {
    setHasAcceptedConsent(true);
    setIsOnboardingVisible(false);
    setIsRecording(true);
  }, [setHasAcceptedConsent]);

  const handleRecorderResult = useCallback(
    transcription => {
      const normalizedTranscription = (transcription || "").trim();
      if (!normalizedTranscription) return;

      const nextValue = appendTranscriptionToRichTextValue(
        value,
        normalizedTranscription
      );

      onChange(nextValue);
      setTranscriptionNoticeVisible(true);
      setIsRecording(false);
    },
    [onChange, value]
  );

  const handleRecorderCancel = useCallback(() => {
    setIsRecording(false);
  }, []);

  const editorClassName = classNames("speech-to-text-rich-text__editor", {
    "speech-to-text-rich-text__editor--with-transcription-notice":
      isTranscriptionNoticeVisible
  });

  return (
    <div className="speech-to-text-rich-text">
      {isEditorVisible && (
        <div className={editorClassName}>
          <FrontEditor
            labelNode={labelNode}
            value={value}
            onChange={onChange}
            height={300}
            attachmentToken={attachmentToken}
            onFocus={onFocus}
          />

          {canRecord && (
            <Button
              type="button"
              className="btn btn-primary btn-sm text-primary-auto-contrast speech-to-text-rich-text__toolbar-button"
              onClick={handleOpenSpeechInput}
              iconClassName="fa fa-microphone"
              label={startRecordingLabel}
            />
          )}

          {isTranscriptionNoticeVisible && (
            <div className="speech-to-text-rich-text__notice">
              <Icon className="icon-infos speech-to-text-rich-text__notice-icon" />
              <span>{t(intlMessages.transcriptionCompleted)}</span>
            </div>
          )}
        </div>
      )}

      {isOnboardingVisible && (
        /* The `border-primary`, `text-primary-*` and `bg-primary-*` utilities come
           from the runtime platform styles (shared/_platform_styles.html.erb) so the
           left bar, the info icon and the action buttons all follow the platform
           primary color with readable text. */
        <div className="speech-to-text-rich-text__onboarding box border-primary margin-bottom-xs">
          <div className="speech-to-text-rich-text__onboarding-content">
            <span className="speech-to-text-rich-text__onboarding-icon text-primary-on-white">
              <Icon className="icon-infos" />
            </span>

            <div>
              <p className="speech-to-text-rich-text__onboarding-title margin-bottom-xxs">
                {t(intlMessages.onboardingTitle)}
              </p>

              <p className="speech-to-text-rich-text__onboarding-description margin-bottom-xs">
                {t(intlMessages.onboardingDescription)}
              </p>

              <div className="speech-to-text-rich-text__onboarding-actions">
                <Button
                  type="button"
                  className="btn btn-sm border-primary bg-primary-inverse bg-primary-hover text-primary-auto-contrast-hover speech-to-text-rich-text__onboarding-later"
                  onClick={dismissOnboarding}
                  label={laterLabel}
                />

                <Button
                  type="button"
                  className="btn btn-primary btn-sm text-primary-auto-contrast"
                  onClick={handleStartRecording}
                  label={startFromOnboardingLabel}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {isRecording && (
        <SpeechToTextRecorder
          autoStart
          resourceId={resourceId}
          onResult={handleRecorderResult}
          onCancel={handleRecorderCancel}
          className="margin-top-xs"
        />
      )}
    </div>
  );
};

export default SpeechToTextRichTextInput;

const intlMessages = defineMessages({
  later: {
    id: "speechToTextRichText.later",
    defaultMessage: "Plus tard"
  },
  onboardingTitle: {
    id: "speechToTextRichText.onboardingTitle",
    defaultMessage: "Vous avez la parole, on vous écoute."
  },
  onboardingDescription: {
    id: "speechToTextRichText.onboardingDescription",
    defaultMessage:
      "Votre message audio sera transmis à notre prestataire de transcription, " +
      "Mistral AI, pour être convertie en texte. L'enregistrement audio n'est pas " +
      "conservé et est uniquement utilisé pour assurer la transcription."
  },
  startFromOnboarding: {
    id: "speechToTextRichText.startFromOnboarding",
    defaultMessage: "J'ai compris, je commence"
  },
  startRecording: {
    id: "speechToTextRichText.startRecording",
    defaultMessage: "Parler"
  },
  transcriptionCompleted: {
    id: "speechToTextRichText.transcriptionCompleted",
    defaultMessage:
      "La transcription est terminée, vous pouvez modifier librement le texte"
  }
});
